import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';
import BirdMascot from './BirdMascot';

const AddToHomeScreen = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 10 seconds on the page
      setTimeout(() => {
        if (!localStorage.getItem('pwa-prompt-dismissed')) {
          setShowPrompt(true);
        }
      }, 10000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setIsDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if installed, dismissed, or no prompt available
  if (isInstalled || isDismissed || !deferredPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50"
        >
          <div className="bg-gradient-to-br from-card to-card/80 backdrop-blur-lg border-2 border-primary/30 rounded-2xl shadow-2xl p-4 relative overflow-hidden">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted/50 transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>

            {/* Content */}
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0">
                <BirdMascot size={48} color="#FF6B6B" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg mb-1 text-gradient-primary">
                  Install JailBirds
                </h3>
                <p className="text-sm text-muted-foreground">
                  Add to your home screen for quick access and a better experience!
                </p>
              </div>
            </div>

            {/* Install button */}
            <Button
              onClick={handleInstallClick}
              className="w-full bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
              size="lg"
            >
              <Download className="w-4 h-4 mr-2" />
              Add to Home Screen
            </Button>

            {/* Visual indicator */}
            <div className="absolute -bottom-1 -right-1 opacity-10">
              <Smartphone className="w-24 h-24 text-primary" />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddToHomeScreen;